// appActions.js
// All context actions for AppContext
import { mockApiCalls } from '../../utils/mockData';
// InvitationApi is imported here for reference when switching from mock to real API.
// See the "SWITCH TO REAL API" comments inside each invitation action below.
import InvitationApi from '../../utils/api/InvitationApi'; // eslint-disable-line no-unused-vars

export function createAppActions(state, dispatch, createNotification) {
    return {
        // Existing actions (maintaining backward compatibility)
        //setVisitors: (visitors) => dispatch({ type: "SETVISITORS", payload: visitors }),
        //setGuidVisitor: (guidVisitor) => dispatch({ type: "SETGUIDVISITOR", payload: guidVisitor }),
        //setUser: (user) => dispatch({ type: "SETUSER", payload: user }),
        //setVisitorSubmited: (visitor) => dispatch({ type: "SETVISITORSUBMITED", payload: visitor }),

        // New enhanced actions
        setLoading: (loading) => dispatch({ type: "SET_LOADING", payload: loading }),
        setError: (error) => dispatch({ type: "SET_ERROR", payload: error }),
        clearError: () => dispatch({ type: "CLEAR_ERROR" }),

        // Authentication
        login: async (credentials) => {
            try {
                dispatch({ type: "SET_LOADING", payload: true });
                dispatch({ type: "CLEAR_ERROR" });

                const response = await mockApiCalls.login(credentials);
                if (response.success) {
                    dispatch({ type: "SETUSER", payload: response.data.user });
                    createNotification('Login successful!', 'success');

                    // Store token
                    localStorage.setItem('token', response.data.token);
                    return response;
                }
            } catch (error) {
                const errorData = JSON.parse(error.message);
                dispatch({ type: "SET_ERROR", payload: errorData.message });
                createNotification(errorData.message, 'error');
                throw error;
            } finally {
                dispatch({ type: "SET_LOADING", payload: false });
            }
        },

        logout: () => {
            localStorage.removeItem('token');
            dispatch({ type: "LOGOUT" });
            createNotification('Logged out successfully', 'info');
        },

        // get visitors
        fetchVisitors: async (homeNumber) => {
            try {
                dispatch({ type: "SET_LOADING", payload: true });
                dispatch({ type: "CLEAR_ERROR" });

                const response = await mockApiCalls.getVisitorsByHome(homeNumber);
                if (response.success) {
                    dispatch({ type: "SETVISITORS", payload: response.data.visitors });
                    return response;
                }
            } catch (error) {
                const errorData = JSON.parse(error.message);
                dispatch({ type: "SET_ERROR", payload: errorData });
                createNotification(errorData.message, 'error');
                throw error;
            } finally {
                dispatch({ type: "SET_LOADING", payload: false });
            }
        },

        // create single visitor
        createVisitor: async (visitor) => {
            try {
                dispatch({ type: "SET_LOADING", payload: true });
                dispatch({ type: "CLEAR_ERROR" });

                const response = await mockApiCalls.createVisitor(visitor);
                if (response.success) {
                    dispatch({ type: "SETVISITORSUBMITED", payload: response.data.visitor });
                    return response;
                }
            } catch (error) {
                const errorData = JSON.parse(error.message);
                dispatch({ type: "SET_ERROR", payload: errorData });
                createNotification(errorData.message, 'error');
                throw error;
            } finally {
                dispatch({ type: "SET_LOADING", payload: false });
            }
        },
        // get visitor by guid
        getVisitorByGuid: async (guid) => {
            try {
                dispatch({ type: "SET_LOADING", payload: true });
                dispatch({ type: "CLEAR_ERROR" });

                const response = await mockApiCalls.getVisitorByGuid(guid);
                if (response.success) {
                    dispatch({ type: "SETGUIDVISITOR", payload: response.data.visitor });
                    return response;
                }
            } catch (error) {
                const errorData = JSON.parse(error.message);
                dispatch({ type: "SET_ERROR", payload: errorData });
                createNotification(errorData.message, 'error');
                throw error;
            } finally {
                dispatch({ type: "SET_LOADING", payload: false });
            }
        },

        // Amenities API methods
        fetchAmenitiesByHome: async (homeNumber) => {
            try {
                dispatch({ type: "SET_LOADING", payload: true });
                dispatch({ type: "CLEAR_ERROR" });

                const response = await mockApiCalls.getAmenitiesByHome(homeNumber);
                if (response.success) {
                    dispatch({ type: "SETHOMEAMENITIES", payload: response.data });
                    return response;
                }
            } catch (error) {
                const errorData = JSON.parse(error.message);
                dispatch({ type: "SET_ERROR", payload: errorData });
                createNotification(errorData.message, 'error');
                throw error;
            } finally {
                dispatch({ type: "SET_LOADING", payload: false });
            }
        },

        fetchAmenities: async () => {
            try {
                dispatch({ type: "CLEAR_ERROR" });

                const response = await mockApiCalls.getAmenitiesPreferences();
                if (response.success) {
                    dispatch({ type: "SETAMENITIES", payload: response.data });
                    return response;
                }
            } catch (error) {
                const errorData = JSON.parse(error.message);
                dispatch({ type: "SET_ERROR", payload: errorData });
                createNotification(errorData.message, 'error');
                throw error;
            }
        },

        addAmenity: async (amenity) => {
            try {
                dispatch({ type: "CLEAR_ERROR" });

                // Call the API to create the amenity
                const response = await mockApiCalls.createAmenity(amenity);
                if (response.success) {
                    // Add the new amenity to the current state
                    const updatedAmenities = [...state.amenities, response.data];
                    dispatch({ type: "SETAMENITIES", payload: updatedAmenities });
                    createNotification('Amenidad creada con éxito', 'success');
                    return response;
                }
            } catch (error) {
                const errorData = JSON.parse(error.message);
                dispatch({ type: "SET_ERROR", payload: errorData });
                createNotification(errorData.message, 'error');
                throw error;
            }
        },

        updateAmenity: async (amenity) => {
            let oldAmenity = null;
            try {
                dispatch({ type: "CLEAR_ERROR" });

                // Store the old amenity for potential revert
                oldAmenity = state.amenities.find(a => a.id === amenity.id);
                
                // Optimistic update - update UI immediately
                const updatedAmenities = state.amenities.map(a => 
                    a.id === amenity.id ? amenity : a
                );
                dispatch({ type: "SETAMENITIES", payload: updatedAmenities });

                // Then call the API
                const response = await mockApiCalls.updateAmenity(amenity);
                if (response.success) {
                    createNotification('Amenidad actualizada con éxito', 'success');
                    return response;
                }
            } catch (error) {
                // If API fails, revert to old amenity
                if (oldAmenity) {
                    const revertedAmenities = state.amenities.map(a => 
                        a.id === amenity.id ? oldAmenity : a
                    );
                    dispatch({ type: "SETAMENITIES", payload: revertedAmenities });
                }
                const errorData = JSON.parse(error.message);
                dispatch({ type: "SET_ERROR", payload: errorData });
                createNotification(errorData.message, 'error');
                throw error;
            }
        },

        deleteAmenity: async (id) => {
            let amenityToDelete = null;
            try {
                dispatch({ type: "CLEAR_ERROR" });

                // Store the amenity for potential revert
                amenityToDelete = state.amenities.find(a => a.id === id);
                
                // Optimistic update - remove from UI immediately
                const updatedAmenities = state.amenities.filter(a => a.id !== id);
                dispatch({ type: "SETAMENITIES", payload: updatedAmenities });

                // Then call the API
                const response = await mockApiCalls.deleteAmenity(id);
                if (response.success) {
                    createNotification('Amenidad eliminada con éxito', 'success');
                    return response;
                }
            } catch (error) {
                // If API fails, restore the amenity
                if (amenityToDelete) {
                    const restoredAmenities = [...state.amenities, amenityToDelete];
                    dispatch({ type: "SETAMENITIES", payload: restoredAmenities });
                }
                const errorData = JSON.parse(error.message);
                dispatch({ type: "SET_ERROR", payload: errorData });
                createNotification(errorData.message, 'error');
                throw error;
            }
        },

        reserveAmenity: async (amenity) => {
            try {
                dispatch({ type: "SET_LOADING", payload: true });
                dispatch({ type: "CLEAR_ERROR" });

                const response = await mockApiCalls.reserveAmenity(amenity);
                if (response.success) {
                    dispatch({ type: "SETAMENITYSUBMITED", payload: response.data });
                    createNotification('Amenidad reservada con éxito', 'success');
                    return response;
                }
            } catch (error) {
                const errorData = JSON.parse(error.message);
                dispatch({ type: "SET_ERROR", payload: errorData });
                createNotification(errorData.message, 'error');
                throw error;
            } finally {
                dispatch({ type: "SET_LOADING", payload: false });
            }
        },

        getReservedAmenitiesByType: async (type) => {
            try {
                dispatch({ type: "SET_LOADING", payload: true });
                dispatch({ type: "CLEAR_ERROR" });

                const response = await mockApiCalls.getReservedAmenitiesByType(type);
                if (response.success) {
                    dispatch({ type: "SETRESERVEDAMENITIES", payload: response.data });
                    return response;
                }
            } catch (error) {
                const errorData = JSON.parse(error.message);
                dispatch({ type: "SET_ERROR", payload: errorData });
                createNotification(errorData.message, 'error');
                throw error;
            } finally {
                dispatch({ type: "SET_LOADING", payload: false });
            }
        },

        getAllReservations: async () => {
            try {
                dispatch({ type: "CLEAR_ERROR" });

                const response = await mockApiCalls.getAllReservations();
                if (response.success) {
                    dispatch({ type: "SETRESERVEDAMENITIES", payload: response.data });
                    return response;
                }
            } catch (error) {
                const errorData = JSON.parse(error.message);
                dispatch({ type: "SET_ERROR", payload: errorData });
                createNotification(errorData.message, 'error');
                throw error;
            }
        },

        // Amenity Reservations CRUD
        addReservation: async (reservation) => {
            try {
                dispatch({ type: "SET_LOADING", payload: true });
                dispatch({ type: "CLEAR_ERROR" });

                const response = await mockApiCalls.createReservation(reservation);
                if (response.success) {
                    // Update reservations in global state
                    const updatedReservations = [...state.reservedAmenities, response.data];
                    dispatch({ type: "SETRESERVEDAMENITIES", payload: updatedReservations });
                    createNotification('Reservación creada con éxito', 'success');
                    return response;
                }
            } catch (error) {
                const errorData = JSON.parse(error.message);
                dispatch({ type: "SET_ERROR", payload: errorData });
                createNotification(errorData.message, 'error');
                throw error;
            } finally {
                dispatch({ type: "SET_LOADING", payload: false });
            }
        },

        updateReservation: async (reservation) => {
            try {
                dispatch({ type: "SET_LOADING", payload: true });
                dispatch({ type: "CLEAR_ERROR" });

                const response = await mockApiCalls.updateReservation(reservation);
                if (response.success) {
                    // Update reservations in global state
                    const updatedReservations = state.reservedAmenities.map(r =>
                        r.id === reservation.id ? response.data : r
                    );
                    dispatch({ type: "SETRESERVEDAMENITIES", payload: updatedReservations });
                    createNotification('Reservación actualizada con éxito', 'success');
                    return response;
                }
            } catch (error) {
                const errorData = JSON.parse(error.message);
                dispatch({ type: "SET_ERROR", payload: errorData });
                createNotification(errorData.message, 'error');
                throw error;
            } finally {
                dispatch({ type: "SET_LOADING", payload: false });
            }
        },

        deleteReservation: async (id) => {
            try {
                dispatch({ type: "SET_LOADING", payload: true });
                dispatch({ type: "CLEAR_ERROR" });

                const response = await mockApiCalls.deleteReservation(id);
                if (response.success) {
                    // Update reservations in global state
                    const updatedReservations = state.reservedAmenities.filter(r => r.id !== id);
                    dispatch({ type: "SETRESERVEDAMENITIES", payload: updatedReservations });
                    createNotification('Reservación eliminada con éxito', 'success');
                    return response;
                }
            } catch (error) {
                const errorData = JSON.parse(error.message);
                dispatch({ type: "SET_ERROR", payload: errorData });
                createNotification(errorData.message, 'error');
                throw error;
            } finally {
                dispatch({ type: "SET_LOADING", payload: false });
            }
        },

        approveReservation: async (id) => {
            try {
                dispatch({ type: "CLEAR_ERROR" });

                // Optimistic update - update UI immediately
                const updatedReservations = state.reservedAmenities.map(r =>
                    r.id === id ? { ...r, status: 1 } : r
                );
                dispatch({ type: "SETRESERVEDAMENITIES", payload: updatedReservations });

                // Then call the API
                const response = await mockApiCalls.approveReservation(id);
                if (response.success) {
                    createNotification('Reservación aprobada', 'success');
                    return response;
                }
            } catch (error) {
                // If API fails, revert the optimistic update by fetching fresh data
                try {
                    const refreshResponse = await mockApiCalls.getAllReservations();
                    if (refreshResponse.success) {
                        dispatch({ type: "SETRESERVEDAMENITIES", payload: refreshResponse.data });
                    }
                } catch (refreshError) {
                    console.error('Error refreshing reservations:', refreshError);
                }
                const errorData = JSON.parse(error.message);
                dispatch({ type: "SET_ERROR", payload: errorData });
                createNotification(errorData.message, 'error');
                throw error;
            }
        },

        rejectReservation: async (id) => {
            try {
                dispatch({ type: "CLEAR_ERROR" });

                // Optimistic update - update UI immediately
                const updatedReservations = state.reservedAmenities.map(r =>
                    r.id === id ? { ...r, status: 2 } : r
                );
                dispatch({ type: "SETRESERVEDAMENITIES", payload: updatedReservations });

                // Then call the API
                const response = await mockApiCalls.rejectReservation(id);
                if (response.success) {
                    createNotification('Reservación rechazada', 'success');
                    return response;
                }
            } catch (error) {
                // If API fails, revert the optimistic update by fetching fresh data
                try {
                    const refreshResponse = await mockApiCalls.getAllReservations();
                    if (refreshResponse.success) {
                        dispatch({ type: "SETRESERVEDAMENITIES", payload: refreshResponse.data });
                    }
                } catch (refreshError) {
                    console.error('Error refreshing reservations:', refreshError);
                }
                const errorData = JSON.parse(error.message);
                dispatch({ type: "SET_ERROR", payload: errorData });
                createNotification(errorData.message, 'error');
                throw error;
            }
        },

        // get payments
        fetchPayments: async (homeNumber) => {
            try {
                dispatch({ type: "SET_LOADING", payload: true });
                dispatch({ type: "CLEAR_ERROR" });

                const response = await mockApiCalls.getPaymentsByHome(homeNumber);
                if (response.success) {
                    dispatch({ type: "SETPAYMENTS", payload: response.data.payments });
                    return response;
                }
            } catch (error) {
                const errorData = JSON.parse(error.message);
                dispatch({ type: "SET_ERROR", payload: errorData });
                createNotification(errorData.message, 'error');
                throw error;
            } finally {
                dispatch({ type: "SET_LOADING", payload: false });
            }
        },

        // create single payment
        createPayment: async (payments) => {
            try {
                dispatch({ type: "SET_LOADING", payload: true });
                dispatch({ type: "CLEAR_ERROR" });

                const response = await mockApiCalls.createPayment(payments);
                if (response.success) {
                    dispatch({ type: "SETPAYMENTSUBMITED", payload: response.data.payment });
                    createNotification('Pago creado con éxito', 'success');
                    return response;
                }
            } catch (error) {
                const errorData = JSON.parse(error.message);
                dispatch({ type: "SET_ERROR", payload: errorData });
                createNotification(errorData.message, 'error');
                throw error;
            } finally {
                dispatch({ type: "SET_LOADING", payload: false });
            }
        },

        // get payments
        fetchAllPayments: async () => {
            try {
                dispatch({ type: "SET_LOADING", payload: true });
                dispatch({ type: "CLEAR_ERROR" });

                const response = await mockApiCalls.fetchAllPayments();
                if (response.success) {
                    dispatch({ type: "SET_ALL_PAYMENTS", payload: response.data.payments });
                    return response;
                }
            } catch (error) {
                const errorData = JSON.parse(error.message);
                dispatch({ type: "SET_ERROR", payload: errorData });
                createNotification(errorData.message, 'error');
                throw error;
            } finally {
                dispatch({ type: "SET_LOADING", payload: false });
            }
        },

        fetchAnnouncements: async (limit) => {
            try {
                dispatch({ type: "SET_LOADING", payload: true });
                const response = await mockApiCalls.getAnnouncements(limit);
                if (response.success) {
                    dispatch({ type: "SET_ANNOUNCEMENTS", payload: response.data });
                }
            } catch (error) {
                dispatch({ type: "SET_ERROR", payload: error.message });
            } finally {
                dispatch({ type: "SET_LOADING", payload: false });
            }
        },

        // Announcements CRUD
        createAnnouncement: async (announcement) => {
            try {
                dispatch({ type: "SET_LOADING", payload: true });
                dispatch({ type: "CLEAR_ERROR" });
                // Here you could call an API, for now just update local state
                const newAnnouncement = { ...announcement, id: Date.now() };
                const updated = [...state.announcements, newAnnouncement];
                dispatch({ type: "SET_ANNOUNCEMENTS", payload: updated });
                createNotification('Anuncio creado', 'success');
            } catch (error) {
                dispatch({ type: "SET_ERROR", payload: error.message });
                createNotification(error.message, 'error');
                throw error;
            } finally {
                dispatch({ type: "SET_LOADING", payload: false });
            }
        },
        updateAnnouncement: async (announcement) => {
            try {
                dispatch({ type: "SET_LOADING", payload: true });
                dispatch({ type: "CLEAR_ERROR" });
                // Here you could call an API, for now just update local state
                const updated = state.announcements.map(a => a.id === announcement.id ? announcement : a);
                dispatch({ type: "SET_ANNOUNCEMENTS", payload: updated });
                createNotification('Anuncio actualizado', 'success');
            } catch (error) {
                dispatch({ type: "SET_ERROR", payload: error.message });
                createNotification(error.message, 'error');
                throw error;
            } finally {
                dispatch({ type: "SET_LOADING", payload: false });
            }
        },
        deleteAnnouncement: async (id) => {
            try {
                dispatch({ type: "SET_LOADING", payload: true });
                dispatch({ type: "CLEAR_ERROR" });
                // Here you could call an API, for now just update local state
                const updated = state.announcements.filter(a => a.id !== id);
                dispatch({ type: "SET_ANNOUNCEMENTS", payload: updated });
                createNotification('Anuncio eliminado', 'success');
            } catch (error) {
                dispatch({ type: "SET_ERROR", payload: error.message });
                createNotification(error.message, 'error');
                throw error;
            } finally {
                dispatch({ type: "SET_LOADING", payload: false });
            }
        },

        // Content fetching
        fetchTestimonials: async (limit) => {
            try {
                dispatch({ type: "SET_LOADING", payload: true });
                const response = await mockApiCalls.getTestimonials(limit);
                if (response.success) {
                    dispatch({ type: "SET_TESTIMONIALS", payload: response.data });
                }
            } catch (error) {
                dispatch({ type: "SET_ERROR", payload: error.message });
            } finally {
                dispatch({ type: "SET_LOADING", payload: false });
            }
        },

        fetchPricing: async () => {
            try {
                const response = await mockApiCalls.getPricing();
                if (response.success) {
                    dispatch({ type: "SET_PRICING", payload: response.data });
                }
            } catch (error) {
                dispatch({ type: "SET_ERROR", payload: error.message });
            }
        },

        fetchBlogPosts: async (page, limit) => {
            try {
                dispatch({ type: "SET_LOADING", payload: true });
                const response = await mockApiCalls.getBlogPosts(page, limit);
                if (response.success) {
                    dispatch({ type: "SET_BLOG", payload: response.data.posts });
                }
            } catch (error) {
                dispatch({ type: "SET_ERROR", payload: error.message });
            } finally {
                dispatch({ type: "SET_LOADING", payload: false });
            }
        },

        fetchFaqs: async () => {
            try {
                const response = await mockApiCalls.getFaqs();
                if (response.success) {
                    dispatch({ type: "SET_FAQS", payload: response.data });
                }
            } catch (error) {
                dispatch({ type: "SET_ERROR", payload: error.message });
            }
        },

        fetchFeatures: async () => {
            try {
                const response = await mockApiCalls.getFeatures();
                if (response.success) {
                    dispatch({ type: "SET_FEATURES", payload: response.data });
                }
            } catch (error) {
                dispatch({ type: "SET_ERROR", payload: error.message });
            }
        },

        // Owners API methods
        fetchOwners: async () => {
            try {
                dispatch({ type: "CLEAR_ERROR" });
                const response = await mockApiCalls.fetchOwners();
                if (response.success) {
                    dispatch({ type: "SET_OWNERS", payload: response.data.owners });
                    return response;
                }
            } catch (error) {
                const errorData = JSON.parse(error.message);
                dispatch({ type: "SET_ERROR", payload: errorData });
                createNotification(errorData.message, 'error');
                throw error;
            }
        },

        createOwner: async (owner) => {
            try {
                dispatch({ type: "CLEAR_ERROR" });

                // Call the API to create the owner
                const response = await mockApiCalls.createOwner(owner);
                if (response.success) {
                    // Add the new owner to the current state
                    const updatedOwners = [...state.owners, response.data.owner];
                    dispatch({ type: "SET_OWNER_SUBMITED", payload: response.data.owner });
                    dispatch({ type: "SET_OWNERS", payload: updatedOwners });
                    createNotification('Propietario creado con éxito', 'success');
                    return response;
                }
            } catch (error) {
                const errorData = JSON.parse(error.message);
                dispatch({ type: "SET_ERROR", payload: errorData });
                createNotification(errorData.message, 'error');
                throw error;
            }
        },

        updateOwner: async (owner) => {
            let oldOwner = null;
            try {
                dispatch({ type: "CLEAR_ERROR" });

                // Store the old owner for potential revert
                oldOwner = state.owners.find(o => o.ownerId === owner.ownerId);
                
                // Optimistic update - update UI immediately
                const updatedOwners = state.owners.map(o => 
                    o.ownerId === owner.ownerId ? owner : o
                );
                dispatch({ type: "SET_OWNERS", payload: updatedOwners });

                // Then call the API
                const response = await mockApiCalls.updateOwner(owner);
                if (response.success) {
                    dispatch({ type: "SET_OWNER_SUBMITED", payload: response.data.owner });
                    createNotification('Propietario actualizado con éxito', 'success');
                    return response;
                }
            } catch (error) {
                // If API fails, revert to old owner
                if (oldOwner) {
                    const revertedOwners = state.owners.map(o => 
                        o.ownerId === owner.ownerId ? oldOwner : o
                    );
                    dispatch({ type: "SET_OWNERS", payload: revertedOwners });
                }
                const errorData = JSON.parse(error.message);
                dispatch({ type: "SET_ERROR", payload: errorData });
                createNotification(errorData.message, 'error');
                throw error;
            }
        },

        deleteOwner: async (ownerId) => {
            let ownerToDelete = null;
            try {
                dispatch({ type: "CLEAR_ERROR" });

                // Store the owner for potential revert
                ownerToDelete = state.owners.find(o => o.ownerId === ownerId);
                
                // Optimistic update - remove from UI immediately
                const updatedOwners = state.owners.filter(o => o.ownerId !== ownerId);
                dispatch({ type: "SET_OWNERS", payload: updatedOwners });

                // Then call the API
                const response = await mockApiCalls.deleteOwner(ownerId);
                if (response.success) {
                    createNotification('Propietario eliminado con éxito', 'success');
                    return response;
                }
            } catch (error) {
                // If API fails, restore the owner
                if (ownerToDelete) {
                    const restoredOwners = [...state.owners, ownerToDelete];
                    dispatch({ type: "SET_OWNERS", payload: restoredOwners });
                }
                const errorData = JSON.parse(error.message);
                dispatch({ type: "SET_ERROR", payload: errorData });
                createNotification(errorData.message, 'error');
                throw error;
            }
        },

        fetchStats: async () => {
            try {
                const response = await mockApiCalls.getStats();
                if (response.success) {
                    dispatch({ type: "SET_STATS", payload: response.data });
                }
            } catch (error) {
                dispatch({ type: "SET_ERROR", payload: error.message });
            }
        },

        // Form submissions
        submitContact: async (formData) => {
            try {
                dispatch({ type: "SET_LOADING", payload: true });
                dispatch({ type: "CLEAR_ERROR" });

                const response = await mockApiCalls.submitContact(formData);
                if (response.success) {
                    createNotification(response.data.message, 'success');
                    return response;
                }
            } catch (error) {
                dispatch({ type: "SET_ERROR", payload: error.message });
                createNotification(error.message, 'error');
                throw error;
            } finally {
                dispatch({ type: "SET_LOADING", payload: false });
            }
        },

        subscribeNewsletter: async (email) => {
            try {
                dispatch({ type: "SET_LOADING", payload: true });
                const response = await mockApiCalls.subscribeNewsletter(email);
                if (response.success) {
                    createNotification(response.data.message, 'success');
                    return response;
                }
            } catch (error) {
                dispatch({ type: "SET_ERROR", payload: error.message });
                createNotification(error.message, 'error');
                throw error;
            } finally {
                dispatch({ type: "SET_LOADING", payload: false });
            }
        },

        // Notifications
        addNotification: (message, type, duration) => createNotification(message, type, duration),
        removeNotification: (id) => dispatch({ type: "REMOVE_NOTIFICATION", payload: id }),

        // Accounting Domain Actions
        fetchOperations: async () => {
            try {
                dispatch({ type: "CLEAR_ERROR" });
                // For now, just set mock data - can be replaced with real API later
                const mockOperations = [
                    {
                        id: 1,
                        date: "2025-08-01",
                        category: "Mantenimiento",
                        type: "income",
                        amount: 60000,
                        description: "Cuota mensual",
                        reference: "REF123",
                        neighborhood: "Downtown"
                    },
                    {
                        id: 2,
                        date: "2025-08-01",
                        category: "Amenidades",
                        type: "income",
                        amount: 10000,
                        description: "Ingreso por amenidades",
                        reference: "REF200",
                        neighborhood: "Downtown"
                    },
                    // Add more mock data as needed...
                ];
                dispatch({ type: "SET_OPERATIONS", payload: mockOperations });
                return { success: true, data: mockOperations };
            } catch (error) {
                dispatch({ type: "SET_ERROR", payload: error });
                throw error;
            }
        },

        createOperation: async (operation) => {
            try {
                dispatch({ type: "CLEAR_ERROR" });

                // Call the API to create the operation
                const response = await mockApiCalls.createOperation(operation);
                if (response.success) {
                    // Add the new operation to the current state
                    const updatedOperations = [...state.operations, response.data];
                    dispatch({ type: "SET_OPERATIONS", payload: updatedOperations });
                    createNotification('Operación creada con éxito', 'success');
                    return response;
                }
            } catch (error) {
                const errorData = JSON.parse(error.message);
                dispatch({ type: "SET_ERROR", payload: errorData });
                createNotification(errorData.message, 'error');
                throw error;
            }
        },

        updateOperation: async (operation) => {
            let oldOperation = null;
            try {
                dispatch({ type: "CLEAR_ERROR" });

                // Store the old operation for potential revert
                oldOperation = state.operations.find(op => op.id === operation.id);
                
                // Optimistic update - update UI immediately
                const updatedOperations = state.operations.map(op => 
                    op.id === operation.id ? operation : op
                );
                dispatch({ type: "SET_OPERATIONS", payload: updatedOperations });

                // Then call the API
                const response = await mockApiCalls.updateOperation(operation);
                if (response.success) {
                    createNotification('Operación actualizada con éxito', 'success');
                    return response;
                }
            } catch (error) {
                // If API fails, revert to old operation
                if (oldOperation) {
                    const revertedOperations = state.operations.map(op => 
                        op.id === operation.id ? oldOperation : op
                    );
                    dispatch({ type: "SET_OPERATIONS", payload: revertedOperations });
                }
                const errorData = JSON.parse(error.message);
                dispatch({ type: "SET_ERROR", payload: errorData });
                createNotification(errorData.message, 'error');
                throw error;
            }
        },

        deleteOperation: async (id) => {
            let operationToDelete = null;
            try {
                dispatch({ type: "CLEAR_ERROR" });

                // Store the operation for potential revert
                operationToDelete = state.operations.find(op => op.id === id);
                
                // Optimistic update - remove from UI immediately
                const updatedOperations = state.operations.filter(op => op.id !== id);
                dispatch({ type: "SET_OPERATIONS", payload: updatedOperations });

                // Then call the API
                const response = await mockApiCalls.deleteOperation(id);
                if (response.success) {
                    createNotification('Operación eliminada con éxito', 'success');
                    return response;
                }
            } catch (error) {
                // If API fails, restore the operation
                if (operationToDelete) {
                    const restoredOperations = [...state.operations, operationToDelete];
                    dispatch({ type: "SET_OPERATIONS", payload: restoredOperations });
                }
                const errorData = JSON.parse(error.message);
                dispatch({ type: "SET_ERROR", payload: errorData });
                createNotification(errorData.message, 'error');
                throw error;
            }
        },

        autoCalculateIncome: async ({ category, date, neighborhood }) => {
            try {
                // Simulate network delay
                await new Promise(res => setTimeout(res, 300));
                // Simple mock logic: amount = days * base rate * multiplier
                const today = new Date();
                const start = date ? new Date(date) : today;
                const days = Math.max(1, Math.ceil((today - start) / (1000 * 60 * 60 * 24)));
                let base = 0;
                if (category === "Mantenimiento") base = 1000;
                else if (category === "Amenidades") base = 250;
                else base = 100;
                const multiplier = neighborhood && neighborhood !== "" ? 1 : 3;
                return days * base * multiplier;
            } catch (error) {
                throw error;
            }
        },

        // Periods management
        fetchPeriods: async () => {
            try {
                dispatch({ type: "CLEAR_ERROR" });
                const mockPeriods = [
                    { id: 1, startDate: "2025-09-01", endDate: "2025-09-30", status: "open" },
                    { id: 2, startDate: "2025-08-01", endDate: "2025-08-31", status: "open" },
                    { id: 3, startDate: "2025-07-01", endDate: "2025-07-31", status: "closed" }
                ];
                dispatch({ type: "SET_PERIODS", payload: mockPeriods });
                return { success: true, data: mockPeriods };
            } catch (error) {
                dispatch({ type: "SET_ERROR", payload: error });
                throw error;
            }
        },

        createPeriod: async (period) => {
            try {
                dispatch({ type: "CLEAR_ERROR" });

                // Call the API to create the period
                const response = await mockApiCalls.createPeriod(period);
                if (response.success) {
                    // Add the new period to the current state
                    const updatedPeriods = [...state.periods, response.data];
                    dispatch({ type: "SET_PERIODS", payload: updatedPeriods });
                    createNotification('Período creado con éxito', 'success');
                    return response;
                }
            } catch (error) {
                const errorData = JSON.parse(error.message);
                dispatch({ type: "SET_ERROR", payload: errorData });
                createNotification(errorData.message, 'error');
                throw error;
            }
        },

        updatePeriod: async (period) => {
            let oldPeriod = null;
            try {
                dispatch({ type: "CLEAR_ERROR" });

                // Store the old period for potential revert
                oldPeriod = state.periods.find(p => p.id === period.id);
                
                // Optimistic update - update UI immediately
                const updatedPeriods = state.periods.map(p => 
                    p.id === period.id ? { ...p, ...period } : p
                );
                dispatch({ type: "SET_PERIODS", payload: updatedPeriods });

                // Then call the API
                const response = await mockApiCalls.updatePeriod(period);
                if (response.success) {
                    createNotification('Período actualizado con éxito', 'success');
                    return response;
                }
            } catch (error) {
                // If API fails, revert to old period
                if (oldPeriod) {
                    const revertedPeriods = state.periods.map(p => 
                        p.id === period.id ? oldPeriod : p
                    );
                    dispatch({ type: "SET_PERIODS", payload: revertedPeriods });
                }
                const errorData = JSON.parse(error.message);
                dispatch({ type: "SET_ERROR", payload: errorData });
                createNotification(errorData.message, 'error');
                throw error;
            }
        },

        archivePeriod: async (id) => {
            let oldPeriod = null;
            try {
                dispatch({ type: "CLEAR_ERROR" });

                // Store the old period for potential revert
                oldPeriod = state.periods.find(p => p.id === id);
                
                // Optimistic update - archive immediately
                const updatedPeriods = state.periods.map(p => 
                    p.id === id ? { ...p, status: "archived" } : p
                );
                dispatch({ type: "SET_PERIODS", payload: updatedPeriods });

                // Then call the API
                const response = await mockApiCalls.archivePeriod(id);
                if (response.success) {
                    createNotification('Período archivado con éxito', 'success');
                    return response;
                }
            } catch (error) {
                // If API fails, revert to old status
                if (oldPeriod) {
                    const revertedPeriods = state.periods.map(p => 
                        p.id === id ? oldPeriod : p
                    );
                    dispatch({ type: "SET_PERIODS", payload: revertedPeriods });
                }
                const errorData = JSON.parse(error.message);
                dispatch({ type: "SET_ERROR", payload: errorData });
                createNotification(errorData.message, 'error');
                throw error;
            }
        },

        // Catalogs Domain Actions
        fetchCategoryOptions: async () => {
            try {
                dispatch({ type: "CLEAR_ERROR" });
                const mockCategoryOptions = [
                    {
                        id: 1, options: [
                            { id: 1, name: "Mantenimiento" },
                            { id: 2, name: "Amenidades" },
                            { id: 3, name: "Servicios Externos" },
                            { id: 4, name: "Multas" },
                            { id: 5, name: "Extraordinario" },
                            { id: 6, name: "Vigilancia" },
                            { id: 7, name: "Salarios" },
                            { id: 8, name: "Otros" }
                        ]
                    },
                    {
                        id: 2, options: [
                            { id: 1, name: "Mantenimiento" },
                            { id: 2, name: "Amenidades" },
                            { id: 3, name: "Multas" },
                            { id: 4, name: "Extraordinario" }
                        ]
                    }
                ];
                dispatch({ type: "SET_CATEGORY_OPTIONS", payload: mockCategoryOptions });
                return { success: true, data: mockCategoryOptions };
            } catch (error) {
                dispatch({ type: "SET_ERROR", payload: error });
                throw error;
            }
        },

        createCategory: async ({ catalogId, name }) => {
            try {
                dispatch({ type: "CLEAR_ERROR" });

                // Optimistic update - add to UI immediately
                const updatedCategoryOptions = state.categoryOptions.map(catalog => {
                    if (catalog.id === catalogId) {
                        const nextId = catalog.options.length ? Math.max(...catalog.options.map(opt => opt.id)) + 1 : 1;
                        return {
                            ...catalog,
                            options: [{ id: nextId, name }, ...catalog.options]
                        };
                    }
                    return catalog;
                });
                dispatch({ type: "SET_CATEGORY_OPTIONS", payload: updatedCategoryOptions });

                // Then call the API
                const response = await mockApiCalls.createCategory({ catalogId, name });
                if (response.success) {
                    createNotification('Categoría creada con éxito', 'success');
                    return response;
                }
            } catch (error) {
                // If API fails, revert the optimistic update by fetching fresh data
                try {
                    const refreshResponse = await mockApiCalls.fetchCategoryOptions();
                    if (refreshResponse.success) {
                        dispatch({ type: "SET_CATEGORY_OPTIONS", payload: refreshResponse.data });
                    }
                } catch (refreshError) {
                    console.error('Error refreshing categories:', refreshError);
                }
                const errorData = JSON.parse(error.message);
                dispatch({ type: "SET_ERROR", payload: errorData });
                createNotification(errorData.message, 'error');
                throw error;
            }
        },

        updateCategory: async ({ catalogId, categoryId, name }) => {
            let oldCategoryOptions = null;
            try {
                dispatch({ type: "CLEAR_ERROR" });

                // Store the old state for potential revert
                oldCategoryOptions = [...state.categoryOptions];
                
                // Optimistic update - update UI immediately
                const updatedCategoryOptions = state.categoryOptions.map(catalog => {
                    if (catalog.id === catalogId) {
                        return {
                            ...catalog,
                            options: catalog.options.map(opt => 
                                opt.id === categoryId ? { ...opt, name } : opt
                            )
                        };
                    }
                    return catalog;
                });
                dispatch({ type: "SET_CATEGORY_OPTIONS", payload: updatedCategoryOptions });

                // Then call the API
                const response = await mockApiCalls.updateCategory({ catalogId, categoryId, name });
                if (response.success) {
                    createNotification('Categoría actualizada con éxito', 'success');
                    return response;
                }
            } catch (error) {
                // If API fails, revert to old state
                if (oldCategoryOptions) {
                    dispatch({ type: "SET_CATEGORY_OPTIONS", payload: oldCategoryOptions });
                }
                const errorData = JSON.parse(error.message);
                dispatch({ type: "SET_ERROR", payload: errorData });
                createNotification(errorData.message, 'error');
                throw error;
            }
        },

        deleteCategory: async ({ catalogId, categoryId }) => {
            let categoryToDelete = null;
            let oldCategoryOptions = null;
            try {
                dispatch({ type: "CLEAR_ERROR" });

                // Store the category and state for potential revert
                oldCategoryOptions = [...state.categoryOptions];
                const catalog = state.categoryOptions.find(cat => cat.id === catalogId);
                categoryToDelete = catalog ? catalog.options.find(opt => opt.id === categoryId) : null;
                
                // Optimistic update - remove from UI immediately
                const updatedCategoryOptions = state.categoryOptions.map(catalog => {
                    if (catalog.id === catalogId) {
                        return {
                            ...catalog,
                            options: catalog.options.filter(opt => opt.id !== categoryId)
                        };
                    }
                    return catalog;
                });
                dispatch({ type: "SET_CATEGORY_OPTIONS", payload: updatedCategoryOptions });

                // Then call the API
                const response = await mockApiCalls.deleteCategory({ catalogId, categoryId });
                if (response.success) {
                    createNotification('Categoría eliminada con éxito', 'success');
                    return response;
                }
            } catch (error) {
                // If API fails, restore the old state
                if (oldCategoryOptions) {
                    dispatch({ type: "SET_CATEGORY_OPTIONS", payload: oldCategoryOptions });
                }
                const errorData = JSON.parse(error.message);
                dispatch({ type: "SET_ERROR", payload: errorData });
                createNotification(errorData.message, 'error');
                throw error;
            }
        },

        createCatalog: async () => {
            try {
                dispatch({ type: "CLEAR_ERROR" });

                // Call the API to create the catalog
                const response = await mockApiCalls.createCatalog();
                if (response.success) {
                    // Add the new catalog to the current state
                    const updatedCategoryOptions = [...state.categoryOptions, response.data];
                    dispatch({ type: "SET_CATEGORY_OPTIONS", payload: updatedCategoryOptions });
                    createNotification('Catálogo creado con éxito', 'success');
                    return response;
                }
            } catch (error) {
                const errorData = JSON.parse(error.message);
                dispatch({ type: "SET_ERROR", payload: errorData });
                createNotification(errorData.message, 'error');
                throw error;
            }
        },

        // Fraccionamientos Domain Actions
        fetchFraccs: async () => {
            try {
                dispatch({ type: "CLEAR_ERROR" });
                const mockFraccs = [
                    {id: 0, name: "Fraccionamiento General", contact: "+521234567800", representative: "Ana Perez", preserves: "", mantenimiento: 1800, amenities: []},
                    { id: 1, name: "Downtown", contact: "+521234567800", representative: "Ana Perez", preserves: "Alberca, Gimnasio", mantenimiento: 1000, amenities: ["Alberca", "Gimnasio"] },
                    { id: 2, name: "Uptown", contact: "+521234567801", representative: "Luis Gomez", preserves: "Terraza, Asador", mantenimiento: 800, amenities: ["Terraza", "Asador"] }
                ];
                dispatch({ type: "SET_FRACCS", payload: mockFraccs });
                return { success: true, data: mockFraccs };
            } catch (error) {
                dispatch({ type: "SET_ERROR", payload: error });
                throw error;
            }
        },

        addFracc: async (fracc) => {
            try {
                dispatch({ type: "CLEAR_ERROR" });

                // Call the API to create the fracc
                const response = await mockApiCalls.addFracc(fracc);
                if (response.success) {
                    // Add the new fracc to the current state
                    const updatedFraccs = [...state.fraccs, response.data];
                    dispatch({ type: "SET_FRACCS", payload: updatedFraccs });
                    createNotification('Fraccionamiento agregado con éxito', 'success');
                    return response;
                }
            } catch (error) {
                const errorData = JSON.parse(error.message);
                dispatch({ type: "SET_ERROR", payload: errorData });
                createNotification(errorData.message, 'error');
                throw error;
            }
        },

        updateFracc: async (fracc) => {
            let oldFracc = null;
            try {
                dispatch({ type: "CLEAR_ERROR" });

                // Store the old fracc for potential revert
                oldFracc = state.fraccs.find(f => f.id === fracc.id);
                
                // Optimistic update - update UI immediately
                const preserves = fracc.amenities && fracc.amenities.length > 0 ? fracc.amenities.join(", ") : "";
                const updatedFraccs = state.fraccs.map(f => 
                    f.id === fracc.id ? { ...fracc, preserves } : f
                );
                dispatch({ type: "SET_FRACCS", payload: updatedFraccs });

                // Then call the API
                const response = await mockApiCalls.updateFracc(fracc);
                if (response.success) {
                    createNotification('Fraccionamiento actualizado con éxito', 'success');
                    return response;
                }
            } catch (error) {
                // If API fails, revert to old fracc
                if (oldFracc) {
                    const revertedFraccs = state.fraccs.map(f => 
                        f.id === fracc.id ? oldFracc : f
                    );
                    dispatch({ type: "SET_FRACCS", payload: revertedFraccs });
                }
                const errorData = JSON.parse(error.message);
                dispatch({ type: "SET_ERROR", payload: errorData });
                createNotification(errorData.message, 'error');
                throw error;
            }
        },

        deleteFracc: async (id) => {
            let fraccToDelete = null;
            try {
                dispatch({ type: "CLEAR_ERROR" });

                // Store the fracc for potential revert
                fraccToDelete = state.fraccs.find(f => f.id === id);
                
                // Optimistic update - remove from UI immediately
                const updatedFraccs = state.fraccs.filter(f => f.id !== id);
                dispatch({ type: "SET_FRACCS", payload: updatedFraccs });

                // Then call the API
                const response = await mockApiCalls.deleteFracc(id);
                if (response.success) {
                    createNotification('Fraccionamiento eliminado con éxito', 'success');
                    return response;
                }
            } catch (error) {
                // If API fails, restore the fracc
                if (fraccToDelete) {
                    const restoredFraccs = [...state.fraccs, fraccToDelete];
                    dispatch({ type: "SET_FRACCS", payload: restoredFraccs });
                }
                const errorData = JSON.parse(error.message);
                dispatch({ type: "SET_ERROR", payload: errorData });
                createNotification(errorData.message, 'error');
                throw error;
            }
        },

        // ─────────────────────────────────────────────────────────────────────
        // Visitor Invitation Security Flow
        // Implements the TOTP-based QR access control described in
        // docs/AI_INTEGRATION_INSTRUCTIONS.md (sections 3.0b, 3.1, 3.2)
        //
        // MOCK MODE (current):  uses mockApiCalls.* from utils/mockData.js
        //                       → response shape: { success, data, message, timestamp }
        //                       → access payload via response.data
        //
        // SWITCH TO REAL API:   replace mockApiCalls.xxx(args) with InvitationApi.xxx(args)
        //                       → InvitationApi returns data directly (no wrapper object)
        //                       → remove the "if (response.success)" check and use result directly
        //
        // Example swap for getInvitationDetails:
        //   MOCK:  const response = await mockApiCalls.getInvitationByGuid(guid);
        //          dispatch({ ..., payload: response.data });
        //   REAL:  const data = await InvitationApi.getInvitationByGuid(guid);
        //          dispatch({ ..., payload: data });
        // ─────────────────────────────────────────────────────────────────────

        /**
         * Fetches public invitation details by GUID (no auth required).
         * Sets state.currentInvitation with invitationState, visitorName, etc.
         */
        getInvitationDetails: async (invitationGuid) => {
            try {
                dispatch({ type: "SET_LOADING", payload: true });
                dispatch({ type: "CLEAR_ERROR" });

                // ── MOCK (active) ─────────────────────────────────────────
                const response = await mockApiCalls.getInvitationByGuid(invitationGuid);
                if (response.success) {
                    dispatch({ type: "SET_CURRENT_INVITATION", payload: response.data });
                    return response.data;
                }
                // ── SWITCH TO REAL API: replace the 3 lines above with:
                // const data = await InvitationApi.getInvitationByGuid(invitationGuid);
                // dispatch({ type: "SET_CURRENT_INVITATION", payload: data });
                // return data;
            } catch (error) {
                dispatch({ type: "SET_ERROR", payload: error.message });
                throw error;
            } finally {
                dispatch({ type: "SET_LOADING", payload: false });
            }
        },

        /**
         * Requests an OTP to be sent to the visitor's registered phone number.
         * Only relevant when SecurityOptions.OtpVerificationEnabled = true on the server.
         */
        requestOtp: async (invitationGuid) => {
            try {
                dispatch({ type: "SET_LOADING", payload: true });
                dispatch({ type: "CLEAR_ERROR" });

                // ── MOCK (active) ─────────────────────────────────────────
                const response = await mockApiCalls.requestOtp(invitationGuid);
                if (response.success) return response.data;
                // ── SWITCH TO REAL API: replace line above with:
                // return await InvitationApi.requestOtp(invitationGuid);
            } catch (error) {
                dispatch({ type: "SET_ERROR", payload: error.message });
                throw error;
            } finally {
                dispatch({ type: "SET_LOADING", payload: false });
            }
        },

        /**
         * Activates an invitation and receives the TOTP secret seed.
         * The seed is returned directly from this action — the caller MUST store it
         * only in React component state (never in localStorage/sessionStorage).
         *
         * @param {string} invitationGuid
         * @param {string|null} otpCode  — pass null when OTP verification is disabled
         * @returns {{ totpSecretSeed, invitationGuid, validUntil, visitorName }}
         */
        activateInvitation: async (invitationGuid, otpCode = null) => {
            try {
                dispatch({ type: "SET_LOADING", payload: true });
                dispatch({ type: "CLEAR_ERROR" });

                // ── MOCK (active) ─────────────────────────────────────────
                // Do NOT dispatch totpSecretSeed to global state — caller keeps it in local state
                const response = await mockApiCalls.activateInvitation(invitationGuid, otpCode);
                if (response.success) return response.data;
                // ── SWITCH TO REAL API: replace line above with:
                // return await InvitationApi.activateInvitation(invitationGuid, otpCode);
            } catch (error) {
                dispatch({ type: "SET_ERROR", payload: error.message });
                throw error;
            } finally {
                dispatch({ type: "SET_LOADING", payload: false });
            }
        },

        /**
         * Creates a new visitor invitation (homeowner, authenticated).
         * Does NOT send any WhatsApp message — the UI shows a wa.me deep link.
         */
        createInvitation: async (invitationData) => {
            try {
                dispatch({ type: "SET_LOADING", payload: true });
                dispatch({ type: "CLEAR_ERROR" });

                // ── MOCK (active) ─────────────────────────────────────────
                const response = await mockApiCalls.createInvitation(invitationData);
                if (response.success) {
                    createNotification('Invitación creada con éxito', 'success');
                    return response.data;
                }
                // ── SWITCH TO REAL API: replace the 4 lines above with:
                // const data = await InvitationApi.createInvitation(invitationData);
                // createNotification('Invitación creada con éxito', 'success');
                // return data;
            } catch (error) {
                const msg = error.message === "INVALID_INVITATION_DATA"
                    ? "Datos de invitación inválidos. Verifica el número de teléfono."
                    : error.message === "NOT_AUTHORIZED"
                    ? "No tienes autorización para crear invitaciones."
                    : "Error al crear la invitación.";
                createNotification(msg, 'error');
                dispatch({ type: "SET_ERROR", payload: msg });
                throw error;
            } finally {
                dispatch({ type: "SET_LOADING", payload: false });
            }
        },

        /**
         * Cancels an existing invitation (homeowner only).
         */
        cancelInvitation: async (invitationGuid) => {
            try {
                dispatch({ type: "SET_LOADING", payload: true });
                dispatch({ type: "CLEAR_ERROR" });

                // ── MOCK (active) ─────────────────────────────────────────
                await mockApiCalls.cancelInvitation(invitationGuid);
                // ── SWITCH TO REAL API: replace line above with:
                // await InvitationApi.cancelInvitation(invitationGuid);

                // Remove from local visitors list
                const updated = state.visitors.filter(v => v.guid !== invitationGuid);
                dispatch({ type: "SETVISITORS", payload: updated });
                createNotification('Invitación cancelada', 'info');
            } catch (error) {
                createNotification('Error al cancelar la invitación', 'error');
                dispatch({ type: "SET_ERROR", payload: error.message });
                throw error;
            } finally {
                dispatch({ type: "SET_LOADING", payload: false });
            }
        }
    };
}
